import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Button, Form, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import Layout from "../../layout/Layout";
import ImageInput from "../../Component/theme-management/ImageInput";
import StatusSwitch from "../../Component/theme-management/StatusSwitch";
import { themesApi, isAuthError } from "../../helpers/tmClient";
import { THEME_TYPE_OPTIONS } from "../../helpers/tmConstants";

/**
 * ThemeForm — reusable create/edit form for Themes.
 * `mode` toggles create vs. update; `initial` seeds edit mode.
 */
const ThemeForm = ({ mode = "add", initial = null }) => {
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const [values, setValues] = useState({
    name: initial?.name || "",
    category: initial?.category || "",
    theme_type: initial?.theme_type || "",
    premium: initial?.premium ?? false,
    status: initial?.status ?? true,
    lock_screen_color: initial?.lock_screen_color || "#000000",
  });
  // Category options come from the existing "Theme" sub-categories.
  const [categories, setCategories] = useState([]);
  // Newly picked File objects (null until user chooses one).
  const [files, setFiles] = useState({
    thumbnail: null,
    thumbnail_ipad: null,
    preview_image: null,
    preview_image_ipad: null,
    color_preview_image: null,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Load Theme category options once.
  useEffect(() => {
    (async () => {
      try {
        const res = await themesApi.categories();
        if (isAuthError(res)) return;
        setCategories(res.data.data || []);
      } catch { /* non-fatal — dropdown just stays empty */ }
    })();
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((v) => ({ ...v, [name]: type === "checkbox" ? checked : value }));
    setErrors((er) => ({ ...er, [name]: null }));
  };
  const onSwitch = (name) => (checked) =>
    setValues((v) => ({ ...v, [name]: checked }));
  const onFile = (key) => (file) => {
    setFiles((f) => ({ ...f, [key]: file }));
    setErrors((er) => ({ ...er, [key]: null }));
  };

  /** Client-side validation mirroring the backend rules. */
  const validate = () => {
    const e = {};
    if (!values.name.trim()) e.name = "Theme name is required";
    if (!values.theme_type) e.theme_type = "Theme type is required";
    if (!isEdit && !files.thumbnail) e.thumbnail = "Thumbnail is required";
    if (!isEdit && !files.preview_image) e.preview_image = "Preview image is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    const fd = new FormData();
    fd.append("name", values.name);
    fd.append("category", values.category || "");
    fd.append("theme_type", values.theme_type);
    fd.append("premium", values.premium);
    fd.append("status", values.status);
    // Color tab fields only apply to lock-screen themes — the backend
    // rejects them otherwise, so only send them when relevant.
    if (values.theme_type === "lock") {
      fd.append("lock_screen_color", values.lock_screen_color || "");
    }
    Object.entries(files).forEach(([k, f]) => {
      if (k === "color_preview_image" && values.theme_type !== "lock") return;
      f && fd.append(k, f);
    });

    setSaving(true);
    try {
      const res = isEdit
        ? await themesApi.update(initial._id, fd)
        : await themesApi.create(fd);
      if (res.data.status) {
        toast.success(res.data.response_message || "Saved", { position: "bottom-right", autoClose: 700 });
        navigate("/themes");
      } else if (res.data.errors) {
        setErrors(res.data.errors);
        toast.error(res.data.response_message || "Validation failed", { position: "bottom-right", autoClose: 1200 });
      } else {
        toast.error(res.data.response_message || "Something went wrong", { position: "bottom-right", autoClose: 1200 });
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      toast.error(data?.response_message || "Request failed", { position: "bottom-right", autoClose: 1200 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout sidebar={true}>
      <div className="page-heading">
        <h3 className="my-1">{isEdit ? "Edit Theme" : "Add Theme"}</h3>
      </div>
      <div className="page-content">
        <Form>
          <Row>
            <Col xs={12}>
              <Card className="mb-4">
                <Card.Body>
                  {saving && <div className="loader table-loader"></div>}
                  <fieldset disabled={saving} style={{ border: "none", padding: 0, margin: 0 }}>
                  <Row>
                    <Col lg={4} sm={12}>
                      <Form.Group>
                        <Form.Label>Theme Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          className="my-2"
                          placeholder="Enter theme name"
                          value={values.name}
                          onChange={onChange}
                          isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col lg={4} sm={12}>
                      <Form.Group>
                        <Form.Label>Category</Form.Label>
                        <Form.Select
                          name="category"
                          className="my-2"
                          value={values.category}
                          onChange={onChange}
                        >
                          <option value="">Select Category</option>
                          {categories.map((c) => (
                            <option key={c._id} value={c.name}>{c.name}</option>
                          ))}
                          {/* Preserve a legacy value not present in the current list. */}
                          {values.category &&
                            !categories.some((c) => c.name === values.category) && (
                              <option value={values.category}>{values.category}</option>
                            )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col lg={4} sm={12}>
                      <Form.Group>
                        <Form.Label>Theme Type <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          name="theme_type"
                          className="my-2"
                          value={values.theme_type}
                          onChange={onChange}
                          isInvalid={!!errors.theme_type}
                        >
                          <option value="">Select Type</option>
                          {THEME_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.theme_type}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col lg={3} sm={6}>
                      <Form.Label className="d-block">Premium</Form.Label>
                      <StatusSwitch checked={values.premium} disabled={saving} onChange={onSwitch("premium")} onLabel="ON" offLabel="OFF" />
                    </Col>
                    <Col lg={3} sm={6}>
                      <Form.Label className="d-block">Active</Form.Label>
                      <StatusSwitch checked={values.status} disabled={saving} onChange={onSwitch("status")} />
                    </Col>
                  </Row>

                  <hr />
                  <h6 className="mb-2">Assets</h6>
                  <Row>
                    <Col lg={3} sm={6}>
                      <ImageInput
                        label="Thumbnail (iPhone)"
                        required={!isEdit}
                        existingUrl={initial?.thumbnail}
                        onChange={onFile("thumbnail")}
                        error={errors.thumbnail}
                        disabled={saving}
                      />
                    </Col>
                    <Col lg={3} sm={6}>
                      <ImageInput
                        label="Thumbnail (iPad)"
                        existingUrl={initial?.thumbnail_ipad}
                        onChange={onFile("thumbnail_ipad")}
                        error={errors.thumbnail_ipad}
                        disabled={saving}
                      />
                    </Col>
                    <Col lg={3} sm={6}>
                      <ImageInput
                        label="Preview Image"
                        required={!isEdit}
                        existingUrl={initial?.preview_image}
                        onChange={onFile("preview_image")}
                        error={errors.preview_image}
                        disabled={saving}
                      />
                    </Col>
                    <Col lg={3} sm={6}>
                      <ImageInput
                        label="Preview Image (iPad)"
                        existingUrl={initial?.preview_image_ipad}
                        onChange={onFile("preview_image_ipad")}
                        error={errors.preview_image_ipad}
                        disabled={saving}
                      />
                    </Col>
                  </Row>

                  {values.theme_type === "lock" && (
                    <>
                      <hr />
                      <h6 className="mb-2">Color</h6>
                      <Row>
                        <Col lg={3} sm={6}>
                          <Form.Group>
                            <Form.Label>Lock Screen Color</Form.Label>
                            <Form.Control
                              type="color"
                              name="lock_screen_color"
                              className="my-2"
                              value={values.lock_screen_color || "#000000"}
                              onChange={onChange}
                              title="Pick a color"
                              isInvalid={!!errors.lock_screen_color}
                            />
                            <Form.Control.Feedback type="invalid">{errors.lock_screen_color}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col lg={3} sm={6}>
                          <ImageInput
                            label="Color Preview Image"
                            existingUrl={initial?.color_preview_image}
                            onChange={onFile("color_preview_image")}
                            error={errors.color_preview_image}
                            disabled={saving}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                  </fieldset>
                </Card.Body>
                <Card.Footer className="text-end">
                  <Button variant="primary" className="me-3 d-inline-flex align-items-center gap-2" onClick={submit} disabled={saving}>
                    {saving && <span className="spinner-border spinner-border-sm" role="status"></span>}
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  <Link to="/themes"><Button variant="secondary" disabled={saving}>Cancel</Button></Link>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </Layout>
  );
};

export default ThemeForm;
